import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Chip,
  FormHelperText,
  CircularProgress,
  Tooltip,
  IconButton,
  LinearProgress,
  Fade,
  Collapse,
} from '@mui/material';
import {
  Close,
  InfoOutlined,
  CheckCircleOutline,
  AutoAwesome,
  RocketLaunch,
  AttachFile,
} from '@mui/icons-material';

/**
 * Normalize enum values from the backend.
 * The LLM may pass enum as:
 *   - An actual array: ["a", "b", "c"] → use as-is
 *   - A comma-separated string: "a, b, c" → split
 *   - A JSON-stringified array: '["a","b"]' → parse
 *   - A single value: "a" → wrap in array
 */
function normalizeEnum(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map(v => String(v).trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    // Try JSON parse first (handles '["a","b"]')
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((v: unknown) => String(v).trim()).filter(Boolean);
        }
      } catch {
        // fall through to comma split
      }
    }
    // Comma-separated
    return trimmed.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
}

// ============================================================================
// TYPES
// ============================================================================

interface FormFieldSchema {
  title: string;
  description: string;
  type: string;
  required?: boolean;
  default?: string | number | boolean;
  enum?: string[];
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  errorMessage?: string;
  format?: string;
}

interface HelpArticle {
  title: string;
  content: string;
  source: 'TechDocs' | 'Knowledge Base' | string;
  url?: string;
}

interface HelpDocs {
  overview?: string;
  articles?: HelpArticle[];
}

interface DynamicFormData {
  type: 'form';
  template_name: string;
  template_title: string;
  parameters_to_ignore: Record<string, unknown>;
  fields: Record<string, FormFieldSchema>;
  help_docs?: HelpDocs;
  /** When 'correction', the form is re-shown with previous values and validation errors */
  mode?: 'new' | 'correction';
  /** Previous parameter values to pre-fill (correction mode) */
  previousValues?: Record<string, string>;
  /** Field-level validation errors from the API (correction mode) */
  fieldErrors?: Record<string, string>;
  /** User-facing explanation of why correction is needed */
  error_message?: string;
}

interface DynamicFormMessageProps {
  formData: DynamicFormData;
  onSubmit: (payload: FormSubmitPayload) => void;
  disabled?: boolean;
  /** External field updates pushed from chat messages (e.g. user types "catalog name is xyz") */
  fieldUpdates?: Record<string, string>;
}

export interface FormSubmitPayload {
  data: {
    parameter_values: Record<string, string>;
    confirmed: boolean;
  };
}

// ============================================================================
// STYLES
// ============================================================================

const COLORS = {
  primary: '#ff8c42',
  primaryDark: '#f07b2a',
  primaryLight: '#ffb380',
  border: '#e0ddd8',
  bgLight: '#fafaf8',
  white: '#fff',
  textPrimary: '#1a1a1a',
  textSecondary: '#444',
  textMuted: '#666',
  error: '#d32f2f',
  success: '#2e7d32',
  shimmer: 'linear-gradient(90deg, transparent 0%, rgba(255,140,66,0.06) 40%, rgba(255,140,66,0.12) 50%, rgba(255,140,66,0.06) 60%, transparent 100%)',
} as const;

// ============================================================================
// CSS ANIMATION KEYFRAMES (injected once)
// ============================================================================

const ANIMATION_STYLE_ID = 'dynamic-form-animations';

function ensureAnimationStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(ANIMATION_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = ANIMATION_STYLE_ID;
  style.textContent = `
    @keyframes dfm-shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes dfm-borderGlow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(255, 140, 66, 0); }
      50% { box-shadow: 0 0 20px 2px rgba(255, 140, 66, 0.15); }
    }
    @keyframes dfm-sparkle {
      0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
      50% { opacity: 1; transform: scale(1) rotate(180deg); }
    }
    @keyframes dfm-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
    @keyframes dfm-gradientMove {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes dfm-fieldReveal {
      from {
        opacity: 0;
        transform: translateY(8px) scale(0.97);
        filter: blur(2px);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
        filter: blur(0);
      }
    }
    @keyframes dfm-checkmark {
      0% { transform: scale(0) rotate(-45deg); opacity: 0; }
      50% { transform: scale(1.2) rotate(0deg); opacity: 1; }
      100% { transform: scale(1) rotate(0deg); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

/** Skeleton placeholder while a field is loading */
const FieldSkeleton: React.FC<{ index: number }> = ({ index }) => (
  <Box
    sx={{
      height: 56,
      borderRadius: '8px',
      background: COLORS.shimmer,
      backgroundSize: '200% 100%',
      animation: 'dfm-shimmer 1.5s ease-in-out infinite',
      animationDelay: `${index * 0.1}s`,
      border: `1px solid ${COLORS.border}`,
    }}
  />
);

/** Sparkle dots that appear during form entrance */
const Sparkles: React.FC<{ show: boolean }> = ({ show }) => {
  if (!show) return null;
  const positions = [
    { top: -6, left: '15%', delay: 0, size: 8 },
    { top: -4, right: '20%', delay: 0.2, size: 6 },
    { top: '30%', left: -6, delay: 0.4, size: 7 },
    { top: '50%', right: -5, delay: 0.1, size: 5 },
    { bottom: -5, left: '40%', delay: 0.3, size: 6 },
  ];
  return (
    <>
      {positions.map((pos, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: pos.size,
            height: pos.size,
            borderRadius: '50%',
            background: COLORS.primary,
            animation: `dfm-sparkle 0.8s ease-out ${pos.delay}s both`,
            pointerEvents: 'none',
            ...pos,
          }}
        />
      ))}
    </>
  );
};

/** File picker that reads the selected file as a base64 data URL, matching
 * the `format: data-url` value the manual DevKits form submits. */
const FileUploadField: React.FC<{
  schema: FormFieldSchema;
  value: string;
  onChange: (val: string) => void;
  error?: string;
  disabled?: boolean;
  isRequired?: boolean;
}> = ({ schema, value, onChange, error, disabled, isRequired }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      onChange(String(reader.result || ''));
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleClear = () => {
    onChange('');
    setFileName('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={handleFileChange}
        disabled={disabled}
      />
      <Button
        variant="outlined"
        size="small"
        fullWidth
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        startIcon={<AttachFile sx={{ fontSize: 16 }} />}
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          borderRadius: '8px',
          fontSize: '0.85rem',
          height: 40,
          color: value ? COLORS.textPrimary : COLORS.textMuted,
          borderColor: error ? COLORS.error : COLORS.border,
          '&:hover': { borderColor: COLORS.primary, bgcolor: 'transparent' },
        }}
      >
        {`${schema.title || 'Attachment'}${isRequired ? ' *' : ''}`}
      </Button>
      {value && (
        <Box sx={{ mt: 0.5 }}>
          <Chip
            label={fileName || 'File selected'}
            size="small"
            onDelete={disabled ? undefined : handleClear}
            sx={{ fontSize: '0.7rem', maxWidth: '100%' }}
          />
        </Box>
      )}
      {(schema.description || error) && (
        <FormHelperText
          sx={{
            fontSize: '0.7rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: error ? COLORS.error : COLORS.textMuted,
          }}
        >
          {error ? error : (
            <>
              <InfoOutlined sx={{ fontSize: 12 }} />
              {schema.description}
            </>
          )}
        </FormHelperText>
      )}
    </Box>
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

export const DynamicFormMessage: React.FC<DynamicFormMessageProps> = ({
  formData,
  onSubmit,
  disabled = false,
  fieldUpdates,
}) => {
  ensureAnimationStyles();

  const fields = formData.fields || {};
  const fieldEntries = Object.entries(fields);
  const totalFields = fieldEntries.length;
  const isCorrection = formData.mode === 'correction';

  // ---- Animation state ----
  // Skip entrance animation in correction mode — show fields immediately
  const [phase, setPhase] = useState<
    'entering' | 'revealing' | 'ready' | 'submitted'
  >(isCorrection ? 'ready' : 'entering');
  const [revealedCount, setRevealedCount] = useState(isCorrection ? totalFields : 0);
  const [showSparkles, setShowSparkles] = useState(!isCorrection);
  const revealTimer = useRef<ReturnType<typeof setTimeout>>();

  // Initialize form values with defaults (or previous values in correction mode)
  const getInitialValues = useCallback(() => {
    // Correction mode: pre-fill from the values that were previously submitted
    if (isCorrection && formData.previousValues) {
      const fromPrev: Record<string, string> = {};
      for (const [key] of fieldEntries) {
        fromPrev[key] = formData.previousValues[key] ?? '';
      }
      return fromPrev;
    }
    const initial: Record<string, string> = {};
    for (const [key, schema] of fieldEntries) {
      if (schema.default !== undefined && schema.default !== null) {
        initial[key] = String(schema.default);
      } else {
        initial[key] = '';
      }
    }
    return initial;
  }, [fieldEntries, isCorrection, formData.previousValues]);

  const [values, setValues] = useState<Record<string, string>>(getInitialValues);
  // Pre-populate errors from fieldErrors in correction mode
  const [errors, setErrors] = useState<Record<string, string>>(
    () => (isCorrection && formData.fieldErrors) ? { ...formData.fieldErrors } : {},
  );
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ---- Apply external field updates from chat messages ----
  useEffect(() => {
    if (!fieldUpdates || Object.keys(fieldUpdates).length === 0) return;
    setValues(prev => {
      const updated = { ...prev };
      for (const [key, value] of Object.entries(fieldUpdates)) {
        if (key in fields) {
          updated[key] = value;
          // Clear any validation error for the updated field
          setErrors(e => {
            if (!e[key]) return e;
            const next = { ...e };
            delete next[key];
            return next;
          });
        }
      }
      return updated;
    });
  }, [fieldUpdates, fields]);

  // ---- Staggered field reveal animation ----
  useEffect(() => {
    // Phase 1: brief "entering" shimmer (400ms)
    const enterTimeout = setTimeout(() => {
      setPhase('revealing');
    }, 400);
    return () => clearTimeout(enterTimeout);
  }, []);

  useEffect(() => {
    if (phase !== 'revealing') return;

    // Reveal fields one-by-one at 120ms intervals
    const INTERVAL = 120;
    let count = 0;

    const tick = () => {
      count += 1;
      setRevealedCount(count);
      if (count < totalFields) {
        revealTimer.current = setTimeout(tick, INTERVAL);
      } else {
        // All fields revealed — transition to ready
        setTimeout(() => {
          setPhase('ready');
          setShowSparkles(false);
        }, 300);
      }
    };
    revealTimer.current = setTimeout(tick, INTERVAL);

    return () => {
      if (revealTimer.current) clearTimeout(revealTimer.current);
    };
  }, [phase, totalFields]);

  const handleChange = (key: string, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
    // Clear error on change
    if (errors[key]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    for (const [key, schema] of fieldEntries) {
      const val = values[key] || '';
      const label = schema.title || key;

      // Required check
      if (schema.required && !val.trim()) {
        newErrors[key] = `${label} is required`;
        continue;
      }

      // Skip further validation if empty and not required
      if (!val.trim()) continue;

      // String length constraints
      if (schema.minLength != null && val.length < schema.minLength) {
        newErrors[key] =
          typeof schema.errorMessage === 'string'
            ? schema.errorMessage
            : `${label} must be at least ${schema.minLength} characters`;
        continue;
      }
      if (schema.maxLength != null && val.length > schema.maxLength) {
        newErrors[key] =
          typeof schema.errorMessage === 'string'
            ? schema.errorMessage
            : `${label} must be at most ${schema.maxLength} characters`;
        continue;
      }

      // Numeric range constraints
      if (schema.minimum != null || schema.maximum != null) {
        const num = Number(val);
        if (!isNaN(num)) {
          if (schema.minimum != null && num < schema.minimum) {
            newErrors[key] = `${label} must be at least ${schema.minimum}`;
            continue;
          }
          if (schema.maximum != null && num > schema.maximum) {
            newErrors[key] = `${label} must be at most ${schema.maximum}`;
            continue;
          }
        }
      }

      // Regex pattern constraint
      if (schema.pattern) {
        try {
          if (!new RegExp(schema.pattern).test(val)) {
            newErrors[key] =
              typeof schema.errorMessage === 'string'
                ? schema.errorMessage
                : `${label} does not match the required format`;
          }
        } catch {
          // Invalid regex in schema — skip validation
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    setSubmitting(true);
    setSubmitted(true);
    setPhase('submitted');

    const payload: FormSubmitPayload = {
      data: {
        parameter_values: { ...values },
        confirmed: true,
      },
    };

    onSubmit(payload);
    setSubmitting(false);
  };

  const handleCancel = () => {
    setSubmitted(true);
    setPhase('submitted');
    const payload: FormSubmitPayload = {
      data: {
        parameter_values: {},
        confirmed: false,
      },
    };
    onSubmit(payload);
  };

  if (submitted) {
    const wasCancelled = !values || Object.values(values).every(v => !v);
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          bgcolor: COLORS.bgLight,
          animation: 'dfm-fieldReveal 0.3s ease-out',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleOutline
            sx={{
              color: wasCancelled ? COLORS.textMuted : COLORS.success,
              fontSize: 20,
              animation: wasCancelled ? 'none' : 'dfm-checkmark 0.5s ease-out',
            }}
          />
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>
            {wasCancelled
              ? 'Form cancelled. Project creation aborted.'
              : `Configuration submitted for "${formData.template_title}"`}
          </Typography>
        </Box>
        {!wasCancelled && (
          <Collapse in timeout={400}>
            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {Object.entries(values)
                .filter(([, v]) => v)
                .map(([key, value], idx) => (
                  <Chip
                    key={key}
                    label={`${fields[key]?.title || key}: ${value}`}
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      bgcolor: 'rgba(255, 140, 66, 0.08)',
                      border: `1px solid rgba(255, 140, 66, 0.2)`,
                      color: COLORS.textSecondary,
                      animation: `dfm-fieldReveal 0.3s ease-out ${idx * 0.05}s both`,
                    }}
                  />
                ))}
            </Box>
          </Collapse>
        )}
      </Paper>
    );
  }

  const isRevealing = phase === 'entering' || phase === 'revealing';
  const progressPercent = totalFields > 0 ? (revealedCount / totalFields) * 100 : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '14px',
        bgcolor: COLORS.white,
        overflow: 'hidden',
        boxShadow: phase === 'ready'
          ? '0 2px 12px rgba(0,0,0,0.08)'
          : '0 0 24px rgba(255, 140, 66, 0.12)',
        animation: isRevealing ? 'dfm-borderGlow 2s ease-in-out infinite' : 'none',
        transition: 'box-shadow 0.5s ease-out',
        position: 'relative',
      }}
    >
      {/* Sparkle decorations */}
      <Sparkles show={showSparkles && isRevealing} />

      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          background: isRevealing
            ? `linear-gradient(135deg, ${COLORS.primary}10 0%, ${COLORS.primary}20 50%, ${COLORS.primary}10 100%)`
            : `linear-gradient(135deg, ${COLORS.primary}08 0%, ${COLORS.primary}15 100%)`,
          backgroundSize: isRevealing ? '200% 100%' : '100% 100%',
          animation: isRevealing ? 'dfm-gradientMove 3s ease infinite' : 'none',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'background 0.5s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isRevealing && (
            <AutoAwesome
              sx={{
                fontSize: 18,
                color: COLORS.primary,
                animation: 'dfm-pulse 1.2s ease-in-out infinite',
              }}
            />
          )}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: '0.9rem',
                color: COLORS.textPrimary,
              }}
            >
              {formData.template_title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: COLORS.textMuted,
                fontSize: '0.75rem',
              }}
            >
              {isRevealing
                ? 'Generating configuration form...'
                : isCorrection
                  ? 'Please correct the highlighted fields and re-submit'
                  : 'Fill in the configuration below to create your project'}
            </Typography>
          </Box>
        </Box>
        {!isRevealing && (
          <Fade in>
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                onClick={handleCancel}
                disabled={disabled}
                sx={{ color: COLORS.textMuted }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Fade>
        )}
      </Box>

      {/* Progress bar during reveal */}
      {isRevealing && (
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 2,
            bgcolor: 'rgba(255,140,66,0.08)',
            '& .MuiLinearProgress-bar': {
              background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
              transition: 'transform 0.15s ease-out',
            },
          }}
        />
      )}

      {/* Form Fields — 2-column grid with staggered reveal */}
      <Box sx={{ px: 2.5, py: 2 }}>
        {/* Correction mode warning banner */}
        {isCorrection && (
          <Fade in timeout={400}>
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: '8px',
                bgcolor: '#fff3e0',
                border: '1px solid #ffe0b2',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <InfoOutlined sx={{ fontSize: 18, color: '#e65100', mt: 0.2 }} />
              <Typography
                variant="body2"
                sx={{ color: '#bf360c', fontSize: '0.82rem', lineHeight: 1.5 }}
              >
                {formData.error_message ||
                  'Some fields need correction. Please fix the highlighted errors below and re-submit.'}
              </Typography>
            </Box>
          </Fade>
        )}
        <Grid container spacing={2}>
        {fieldEntries.map(([key, schema], index) => {
          const isFieldRevealed = index < revealedCount;
          const enumValues = normalizeEnum(schema.enum);
          const hasEnum = enumValues.length > 0;
          const isRequired = schema.required;

          return (
            <Grid item xs={12} sm={6} key={key}>
              {!isFieldRevealed ? (
                <FieldSkeleton index={index} />
              ) : (
              <Box sx={{ animation: 'dfm-fieldReveal 0.35s ease-out both' }}>
              {hasEnum ? (
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors[key]}
                  disabled={disabled || isRevealing}
                >
                  <InputLabel
                    sx={{
                      fontSize: '0.85rem',
                      '&.Mui-focused': { color: COLORS.primary },
                    }}
                  >
                    {schema.title || key}
                    {isRequired ? ' *' : ''}
                  </InputLabel>
                  <Select
                    value={values[key] || ''}
                    label={`${schema.title || key}${isRequired ? ' *' : ''}`}
                    onChange={e => handleChange(key, e.target.value as string)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 240,
                          '& .MuiMenuItem-root': { fontSize: '0.85rem' },
                        },
                      },
                    }}
                    sx={{
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.border,
                        transition: 'border-color 0.2s ease',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.primary,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.primary,
                      },
                    }}
                  >
                    {enumValues.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {(schema.description || errors[key]) && (
                    <FormHelperText
                      sx={{
                        fontSize: '0.7rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: errors[key] ? COLORS.error : COLORS.textMuted,
                      }}
                    >
                      {errors[key] ? (
                        errors[key]
                      ) : (
                        <>
                          <InfoOutlined sx={{ fontSize: 12 }} />
                          {schema.description}
                        </>
                      )}
                    </FormHelperText>
                  )}
                </FormControl>
              ) : schema.format === 'data-url' ? (
                <FileUploadField
                  schema={schema}
                  value={values[key] || ''}
                  onChange={val => handleChange(key, val)}
                  error={errors[key]}
                  disabled={disabled || isRevealing}
                  isRequired={isRequired}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={`${schema.title || key}${isRequired ? ' *' : ''}`}
                  value={values[key] || ''}
                  onChange={e => handleChange(key, e.target.value)}
                  error={!!errors[key]}
                  helperText={
                    errors[key] || (
                      schema.description ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <InfoOutlined sx={{ fontSize: 12 }} />
                          {schema.description}
                          {(schema.minLength != null || schema.maxLength != null) && (
                            <span style={{ marginLeft: 4, opacity: 0.7 }}>
                              ({schema.minLength != null ? `min ${schema.minLength}` : ''}
                              {schema.minLength != null && schema.maxLength != null ? ' / ' : ''}
                              {schema.maxLength != null ? `max ${schema.maxLength}` : ''} chars)
                            </span>
                          )}
                        </span>
                      ) : undefined
                    )
                  }
                  disabled={disabled || isRevealing}
                  placeholder={schema.default ? `Default: ${schema.default}` : undefined}
                  inputProps={{
                    ...(schema.maxLength != null ? { maxLength: schema.maxLength } : {}),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      '& fieldset': {
                        borderColor: COLORS.border,
                        transition: 'border-color 0.2s ease',
                      },
                      '&:hover fieldset': {
                        borderColor: COLORS.primary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: COLORS.primary,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      '&.Mui-focused': { color: COLORS.primary },
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '0.7rem',
                    },
                  }}
                />
              )}
              </Box>
              )}
            </Grid>
          );
        })}
        </Grid>
      </Box>

      {/* Actions — slide up after all fields revealed */}
      <Collapse in={phase === 'ready'} timeout={400}>
        <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          bgcolor: COLORS.bgLight,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: COLORS.textMuted, fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <AutoAwesome sx={{ fontSize: 12, color: COLORS.primary }} />
          {totalFields} fields · Fill required fields to continue
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCancel}
          disabled={disabled || submitting}
          sx={{
            borderColor: COLORS.border,
            color: COLORS.textMuted,
            textTransform: 'none',
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: 500,
            '&:hover': {
              borderColor: COLORS.textMuted,
              bgcolor: 'transparent',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={disabled || submitting}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <RocketLaunch sx={{ fontSize: 14 }} />}
          sx={{
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
            color: COLORS.white,
            textTransform: 'none',
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(255, 140, 66, 0.3)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, #e06a1a 100%)`,
              boxShadow: '0 4px 16px rgba(255, 140, 66, 0.4)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            '&.Mui-disabled': {
              background: '#e8e5e0',
              color: '#b0ada8',
            },
          }}
        >
          {submitting ? 'Creating...' : 'Create Project'}
        </Button>
        </Box>
      </Box>
      </Collapse>

      {/* Generating indicator bar at bottom while revealing */}
      {isRevealing && (
        <Box
          sx={{
            px: 2.5,
            py: 1.2,
            borderTop: `1px solid ${COLORS.border}`,
            bgcolor: COLORS.bgLight,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CircularProgress size={12} sx={{ color: COLORS.primary }} />
          <Typography
            variant="caption"
            sx={{
              color: COLORS.textMuted,
              fontSize: '0.72rem',
              animation: 'dfm-pulse 1.5s ease-in-out infinite',
            }}
          >
            Building form — {revealedCount} of {totalFields} fields loaded
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DynamicFormMessage;
